import logger from '@eventbrite/client-logger';
import { applePay, ApplePay, client as braintreeClient } from 'braintree-web';
import { ApplePayPaymentRequest } from 'braintree-web/modules/apple-pay';
import { Dispatch } from 'redux';
import { change, submit } from 'redux-form';
import { CHECKOUT_FORM_NAME } from '../constants';
import { getTotalAmount } from '../selectors/order';
import { CheckoutState } from '../state';
import { AppNamespace } from '../state/app';
import { EventNamespace } from '../state/event';
import { OrderNamespace } from '../state/order';
import { isApplePayCardActive } from '../utils/applePay';
import {
    trackApplePayCanceled,
    trackApplePayConfirmed,
} from './eventAnalytics';
import { getAndSetBraintreeToken } from './order/common';

/**
 * We need this interface since the types of braintree-web don't include it, but it's there.
 */
export interface ApplePayWithMerchantIdentifier extends ApplePay {
    merchantIdentifier: string;
}

declare global {
    interface Window {
        ApplePaySession: typeof ApplePaySession;
    }
}

export enum ApplePayActionType {
    setApplepayPaymentRequest = 'setApplepayPaymentRequest',
    setApplepayInstance = 'setApplepayInstance',
    setCanMakePaymentsWithActiveCard = 'setCanMakePaymentsWithActiveCard',
    setIsLoadingApplePay = 'setIsLoadingApplePay',
}

export interface SetApplePayPaymentRequestAction {
    type: ApplePayActionType.setApplepayPaymentRequest;
    payload: {
        applepayPaymentRequest: ApplePayPaymentRequest;
    };
}

export interface SetApplePayInstanceAction {
    type: ApplePayActionType.setApplepayInstance;
    payload: {
        applepayInstance: ApplePay;
    };
}

export interface SetCanMakePaymentsWithActiveCardAction {
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard;
    payload: {
        canMakePaymentsWithActiveCard: boolean;
    };
}

export interface SetIsLoadingApplePayAction {
    type: ApplePayActionType.setIsLoadingApplePay;
    payload: {
        isLoadingApplePay: boolean;
    };
}

export const setApplepayPaymentRequest = (
    applepayPaymentRequest: ApplePayPaymentRequest,
): SetApplePayPaymentRequestAction => ({
    type: ApplePayActionType.setApplepayPaymentRequest,
    payload: {
        applepayPaymentRequest,
    },
});

export const setApplepayInstance = (
    applepayInstance: ApplePay,
): SetApplePayInstanceAction => ({
    type: ApplePayActionType.setApplepayInstance,
    payload: {
        applepayInstance,
    },
});

export const setCanMakePaymentsWithActiveCard = (
    canMakePaymentsWithActiveCard: boolean,
): SetCanMakePaymentsWithActiveCardAction => ({
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard,
    payload: {
        canMakePaymentsWithActiveCard,
    },
});

export const setIsLoadingApplePay = (
    isLoadingApplePay: boolean,
): SetIsLoadingApplePayAction => ({
    type: ApplePayActionType.setIsLoadingApplePay,
    payload: {
        isLoadingApplePay,
    },
});

const getApplePayPaymentRequest = async (
    state: CheckoutState,
    applepayInstance: ApplePay,
): Promise<ApplePayPaymentRequest> => {
    const { event, app } = state as {
        order: OrderNamespace;
        event: EventNamespace;
        app: AppNamespace;
    };

    const payRequest = {
        total: {
            label: `Tickets for ${event.name.text}`,
            amount: `${getTotalAmount(state)}`,
        },
        currencyCode: `${event.currency}`,
        merchantCapabilities: [
            'supports3DS',
            'supportsDebit',
            'supportsCredit',
        ],
        supportedNetworks: ['masterCard', 'visa', 'amex', 'discover'],
        countryCode: `${app.defaultUserCountry}`,
    };

    return applepayInstance.createPaymentRequest(payRequest);
};

export const getApplePayInstance = async (order: OrderNamespace) => {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });
    return applePay.create({
        client,
    }) as Promise<ApplePayWithMerchantIdentifier>;
};

export const onApplePayMethodSelected = () => async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    dispatch(setIsLoadingApplePay(true));

    try {
        await dispatch(getAndSetBraintreeToken());

        const state = getState();
        const applepayInstance = await getApplePayInstance(
            state.order as OrderNamespace,
        );
        const applepayPaymentRequest = await getApplePayPaymentRequest(
            state,
            applepayInstance,
        );
        const canMakePaymentsWithActiveCard = await isApplePayCardActive(
            applepayInstance,
        );

        dispatch(setApplepayInstance(applepayInstance));
        dispatch(setApplepayPaymentRequest(applepayPaymentRequest));
        dispatch(
            setCanMakePaymentsWithActiveCard(canMakePaymentsWithActiveCard),
        );
    } catch (error) {
        logger.error(error);
    } finally {
        dispatch(setIsLoadingApplePay(false));
    }
};

export const startApplePaySession = () => (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    const state = getState();
    const { applepay } = state;

    if (
        !window.ApplePaySession ||
        !applepay.applepayInstance ||
        !applepay.applepayPaymentRequest
    ) {
        return;
    }

    try {
        const session = new window.ApplePaySession(
            3,
            applepay.applepayPaymentRequest as ApplePayJS.ApplePayPaymentRequest,
        );

        session.onvalidatemerchant = (event) => {
            (applepay.applepayInstance as ApplePay).performValidation(
                {
                    validationURL: event.validationURL,
                    displayName: 'eventbrite',
                },
                function (err, validationData) {
                    if (err) {
                        logger.error(err);
                        session.abort();
                        return;
                    }
                    session.completeMerchantValidation(validationData);
                },
            );
        };

        session.onpaymentauthorized = (event) => {
            (applepay.applepayInstance as ApplePay).tokenize(
                {
                    token: event.payment.token,
                },
                function (tokenizeErr, payload) {
                    if (tokenizeErr) {
                        logger.error(
                            `Error tokenizing Apple Pay: ${tokenizeErr}`,
                        );
                        return;
                    }

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.applePayBraintreeNonce',
                            payload?.nonce,
                        ),
                    );

                    dispatch(
                        trackApplePayConfirmed(
                            applepay.canMakePaymentsWithActiveCard,
                        ),
                    );

                    // After you have transacted with the payload.nonce,
                    // call `completePayment` to dismiss the Apple Pay sheet.
                    session.completePayment(ApplePaySession.STATUS_SUCCESS);

                    dispatch(submit(CHECKOUT_FORM_NAME));
                },
            );
        };

        session.oncancel = () => {
            dispatch(
                trackApplePayCanceled(applepay.canMakePaymentsWithActiveCard),
            );
            session.completePayment(ApplePaySession.STATUS_FAILURE);
        };

        session.begin();
    } catch (error) {
        logger.error(error);
    }
};

export type ApplePayAction =
    | SetApplePayPaymentRequestAction
    | SetApplePayInstanceAction
    | SetCanMakePaymentsWithActiveCardAction
    | SetIsLoadingApplePayAction;
